import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FiFileText, FiSearch } from 'react-icons/fi';
import { depositFormService, authService } from '../api/services';
import ConfirmDialog from '../components/ConfirmDialog';
import DepositDetailsDialog from '../components/DepositDetailsDialog';
import DepositComplaints from '../components/DepositComplaints';
import { useLanguage } from '../contexts/LanguageContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { deleteLocalDraft, listLocalDrafts } from '../utils/depositDrafts';

const BookingList = () => {
  const { t, language } = useLanguage();
  const locale = language === 'en' ? 'en-US' : 'vi-VN';
  const [bookings, setBookings] = useState([]);
  const [complaintTarget,setComplaintTarget]=useState(null);
  const [searchParams]=useSearchParams();
  const depositFilter=searchParams.get('deposit');
  const fetchGeneration=useRef(0);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [search, setSearch] = useState('');
  const [draftNotice, setDraftNotice] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editError, setEditError] = useState('');
  const [updatingIds, setUpdatingIds] = useState({});
  const [statusError, setStatusError] = useState('');
  const pendingUpdates = useRef(new Set());
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const deletingDraft = bookings.some((booking) => booking.id === bookingToDelete && booking.status === 'draft');

  const extractBookings = (payload) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.data)) {
      return payload.data;
    }

    if (Array.isArray(payload?.data?.data)) {
      return payload.data.data;
    }

    return [];
  };

  const fetchBookings = useCallback(async () => {
    const generation=++fetchGeneration.current;
    try {
      const [savedResult, remoteResult, localResult] = await Promise.allSettled([
        depositFormService.getDeposits(depositFilter?{id:depositFilter}:undefined), depositFormService.getDrafts(), listLocalDrafts(authService.getCurrentUser()?.id),
      ]);
      if(generation!==fetchGeneration.current)return;
      const response = savedResult.status === 'fulfilled' ? savedResult.value : [];
      const deposits = extractBookings(response);
      const drafts = new Map();
      if (remoteResult.status === 'fulfilled') remoteResult.value.forEach((draft) => drafts.set(draft.id, { ...draft, updatedAt: draft.updated_at }));
      if (localResult.status === 'fulfilled') localResult.value.forEach((draft) => {
        const remote = drafts.get(draft.id);
        if (!remote || new Date(draft.updatedAt) >= new Date(remote.updatedAt)) drafts.set(draft.id, { ...draft, form_data: draft.form });
      });
      setDraftNotice(remoteResult.status === 'rejected' ? 'Chưa tải được bản nháp trên hệ thống. Các bản nháp lưu trên thiết bị vẫn hiển thị bên dưới.' : '');
      const draftRows = [...drafts.values()].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map((draft) => ({
        ...draft, status: 'draft', room: { title: draft.form_data.roomCode ? `Phòng ${draft.form_data.roomCode}` : draft.form_data.productId || 'Chưa chọn phòng' },
        user: { name: draft.form_data.fullName || 'Chưa nhập khách hàng' }, start_date: draft.form_data.checkInDate,
        total_price: draft.form_data.depositAmount || 0, notes: Object.values(draft.form_data).filter((value) => typeof value === 'string').join(' '),
      }));
      setBookings([...(depositFilter?[]:draftRows), ...deposits.map((deposit) => {
        const formData = deposit.form_data || {};
        const labels = {
          fullName: 'Họ tên',
          phone: 'Số điện thoại',
          dob: 'Ngày sinh',
          gender: 'Giới tính',
          zalo: 'Zalo',
          cccd: 'CCCD số',
          issueDate: 'Ngày cấp',
          issuePlace: 'Nơi cấp',
          permanentAddress: 'Nơi thường trú',
          hometown: 'Quê quán',
          transactionType: 'Loại giao dịch',
          productId: 'Mã sản phẩm',
          roomCode: 'Mã phòng',
          projectAddress: 'Địa chỉ dự án',
          rent: 'Giá thuê',
          rentalMonths: 'Số tháng',
          checkInDate: 'Ngày dự kiến Checkin',
          occupants: 'Số người ở',
          vehicles: 'Số lượng xe',
          electricity: 'Phí điện',
          water: 'Phí nước',
          parking: 'Phí giữ xe',
          management: 'Phí quản lý',
          otherFee: 'Phí khác',
          depositAmount: 'Cọc giữ phòng',
          depositDate: 'Ngày cọc',
          paymentMethod: 'Phương thức cọc',
          additionalDeposit: 'Cọc bổ sung',
          additionalDepositDate: 'Ngày cọc bổ sung',
        };
        const notes = Object.entries(formData)
          .filter(([, value]) => value !== '' && value !== null && value !== undefined)
          .map(([key, value]) => `${labels[key] || key}: ${value}`)
          .join('\n');

        return {
          ...deposit,
          room: deposit.room,
          user: { ...deposit.user, name: formData.fullName || deposit.user?.name },
          start_date: deposit.start_date,
          total_price: formData.depositAmount || 0,
          notes,
        };
      })]);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [depositFilter]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleStatusUpdate = async (id, status) => {
    if (pendingUpdates.current.has(id)) return;
    pendingUpdates.current.add(id);
    setUpdatingIds((previous) => ({ ...previous, [id]: true })); setStatusError('');
    try {
      const updated = await depositFormService.updateDeposit(id, status);
      setBookings((previous) => previous.map((booking) => booking.id === id ? { ...booking, ...updated, user: { ...updated.user, name: updated.form_data?.fullName || booking.user?.name }, total_price: updated.form_data?.depositAmount ?? booking.total_price, notes: booking.notes } : booking));
    } catch (error) {
      setStatusError(error.response?.data?.message || t('bookingList.updateFailed'));
    } finally {
      pendingUpdates.current.delete(id);
      setUpdatingIds((previous) => ({ ...previous, [id]: false }));
    }
  };

  const handleEdit = async (booking) => {
    if (editingId) return;
    setEditingId(booking.id); setEditError('');
    try {
      const draft = await depositFormService.startEditing(booking.id);
      navigate(`/create-deposit?draft=${draft.id}`);
    } catch (error) { setEditError(error.response?.data?.message || t('bookingList.editFailed')); }
    finally { setEditingId(null); }
  };

  const handleDelete = async (id) => {
    const targetId = bookingToDelete ?? id;
    if (!targetId) {
      return;
    }

    try {
      if (bookings.find((item) => item.id === targetId)?.status === 'draft') {
        try { await depositFormService.deleteDraft(targetId); }
        catch (error) { if (error.response?.status !== 404) throw error; }
        await deleteLocalDraft(targetId).catch(() => {});
        Object.keys(sessionStorage).filter((key) => key.startsWith(`deposit-active-draft:${user.id}`) && sessionStorage.getItem(key) === targetId).forEach((key) => sessionStorage.removeItem(key));
      } else await depositFormService.deleteDeposit(targetId);
      setBookings((prev) => prev.filter((booking) => booking.id !== targetId));
      setBookingToDelete(null);
    } catch (error) {
      alert(t('bookingList.deleteFailed'));
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved': return 'badge-info';
      case 'pending': return 'badge-warning';
      case 'rejected': return 'badge-danger';
      case 'cancelled': return 'badge-danger';
      case 'completed': return 'badge-info';
      default: return 'badge-warning';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'draft': return 'Chưa hoàn thành';
      case 'pending': return t('bookingList.statusPending');
      case 'approved': return t('bookingList.statusCompleted');
      case 'rejected': return t('bookingList.statusRejected');
      case 'cancelled': return t('bookingList.statusCancelled');
      case 'completed': return t('bookingList.statusCompleted');
      default: return status;
    }
  };

  const canManageBookings = ['admin', 'landlord', 'saler'].includes(user?.role);

  const filteredBookings = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return bookings.filter((booking) => {
      const searchableText = [
        booking.id,
        booking.room?.title,
        booking.user?.name,
        booking.notes,
      ].join(' ').toLowerCase();

      return !normalizedSearch || searchableText.includes(normalizedSearch);
    });
  }, [bookings, search]);

  const bookingStats = useMemo(() => ({
    total: bookings.length,
  }), [bookings]);

  if (loading) {
    return (
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Quản lý phiếu cọc</h1>
        </div>
        <div className="skeleton-table">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={`booking-skeleton-${index}`} className="skeleton-table-row">
              <div className="skeleton-shimmer skeleton-line" />
              <div className="skeleton-shimmer skeleton-line" />
              <div className="skeleton-shimmer skeleton-line" />
              <div className="skeleton-shimmer skeleton-line sm" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1 className="page-title">Quản lý phiếu cọc</h1>
        <Link to={`/create-deposit?new=${Date.now()}`} className="btn btn-primary">Tạo phiếu cọc</Link>
      </div>
      {draftNotice && <p role="status" className="alert alert-warning">{draftNotice}</p>}
      {editError && <p role="alert" className="error-message">{editError}</p>}
      {statusError && <p role="alert" className="error-message">{statusError}</p>}
      {depositFilter&&<p>Đang xem phiếu cọc #{depositFilter}. <Link to="/bookings">Xem toàn bộ phiếu cọc</Link></p>}

      <div className="deposit-management-toolbar">
        <div>
          <p className="deposit-management-eyebrow">QUẢN LÝ GIAO DỊCH</p>
          <h2 className="deposit-management-heading">Danh sách phiếu cọc</h2>
          <p className="deposit-management-description">
            Theo dõi và xử lý các phiếu cọc được tạo từ hệ thống.
          </p>
        </div>
        <div className="deposit-management-search">
          <FiSearch size={18} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Tìm mã phiếu, phòng hoặc khách hàng..."
            aria-label="Tìm kiếm phiếu cọc"
          />
        </div>
      </div>

      <div className="deposit-stat-grid">
        <div className="deposit-stat-card">
          <span className="deposit-stat-icon deposit-stat-icon-blue"><FiFileText /></span>
          <div><strong>{bookingStats.total}</strong><span>Tổng phiếu cọc</span></div>
        </div>
      </div>

      {bookings.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <h3 className="empty-state-title">{t('bookingList.emptyTitle')}</h3>
          <p className="empty-state-description">
            {canManageBookings 
              ? t('bookingList.emptyManager')
              : t('bookingList.emptyUser')}
          </p>
        </div>
      ) : filteredBookings.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔎</div>
          <h3 className="empty-state-title">Không tìm thấy phiếu cọc</h3>
          <p className="empty-state-description">Thử thay đổi từ khóa hoặc bộ lọc trạng thái.</p>
        </div>
      ) : (
        <div className="table deposit-list-table">
          <table>
            <colgroup>
              <col style={{ width: '8%' }} /><col style={{ width: '11%' }} /><col style={{ width: '13%' }} />
              <col style={{ width: '13%' }} /><col style={{ width: '13%' }} /><col style={{ width: '13%' }} /><col style={{ width: '29%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('bookingList.colRoom')}</th>
                <th>{t('bookingList.colBooker')}</th>
                <th>{t('bookingList.colStartDate')}</th>
                <th>{t('bookingList.colPrice')}</th>
                <th>{t('bookingList.colStatus')}</th>
                <th>{t('bookingList.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map((booking) => (
                <tr key={booking.id}>
                  <td>{booking.status === 'draft' ? (booking.deposit_form_id ? `Nháp sửa #${booking.deposit_form_id}` : `Nháp ${booking.id.slice(0, 8)}`) : booking.id}</td>
                  <td>{booking.room?.title}</td>
                  <td>{booking.user?.name}</td>
                  <td>{booking.start_date ? new Date(booking.start_date).toLocaleDateString(locale) : '—'}</td>
                  <td className="deposit-list-price">{Number(booking.total_price).toLocaleString(locale)} {t('bookingList.currencyShort')}</td>
                  <td>
                    <span className={`badge ${getStatusBadgeClass(booking.status)}`}>
                      {getStatusText(booking.status)}
                    </span>
                    {booking.status === 'pending' && booking.hold?.expires_at && <small style={{ display:'block' }}>{new Date(booking.hold.expires_at) <= new Date() ? 'Đã hết thời gian giữ phòng' : `Giữ phòng đến ${new Date(booking.hold.expires_at).toLocaleString(locale)}`}</small>}
                  </td>
                  <td>
                    <fieldset className="table-actions deposit-row-actions" disabled={Boolean(updatingIds[booking.id])} aria-busy={Boolean(updatingIds[booking.id])}>
                    {booking.status!=='draft'&&<button type="button" className="btn btn-neutral btn-sm" onClick={()=>setComplaintTarget(booking)}>{booking.complaints?.some((item)=>item.status==='open')?'Xem khiếu nại':'Khiếu nại cọc'}</button>}
                    {booking.status === 'draft' && <>
                      <Link className="btn btn-primary btn-sm" to={`/create-deposit?draft=${booking.id}`}>Tiếp tục nhập</Link>
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => setBookingToDelete(booking.id)}>Xóa bản nháp</button>
                    </>}
                    {booking.status !== 'draft' && (!booking.hold || (booking.hold.status === 'pending' && new Date(booking.hold.expires_at) > new Date())) && (user.role === 'admin' || Number(booking.user_id) === Number(user.id) || Number(booking.room?.owner_id) === Number(user.id)) && <button
                      type="button" className="btn btn-warning btn-sm" disabled={Boolean(editingId)} onClick={() => handleEdit(booking)}
                    >{editingId === booking.id ? t('bookingList.openingEditor') : t('bookingList.edit')}</button>}
                    {booking.status === 'pending' && canManageBookings && (!booking.hold?.expires_at || new Date(booking.hold.expires_at) > new Date()) && (
                      <>
                        <button 
                          onClick={() => handleStatusUpdate(booking.id, 'completed')}
                          className="btn btn-success btn-sm"
                          aria-label={t('bookingList.approveAria', { id: booking.id })}
                        >
                          {t('bookingList.approve')}
                        </button>
                        <button 
                          onClick={() => handleStatusUpdate(booking.id, 'rejected')}
                          className="btn btn-danger btn-sm"
                          aria-label={t('bookingList.rejectAria', { id: booking.id })}
                        >
                          {t('bookingList.reject')}
                        </button>
                      </>
                    )}
                    {booking.status === 'pending' && booking.user_id === user.id && (
                      <button 
                        onClick={() => setBookingToDelete(booking.id)}
                        className="btn btn-danger btn-sm"
                        aria-label={t('bookingList.cancelAria', { id: booking.id })}
                      >
                        {t('bookingList.cancel')}
                      </button>
                    )}
                    {booking.status !== 'draft' && canManageBookings && booking.notes && (
                      <button
                        onClick={() => setSelectedBooking(booking)}
                        className="btn btn-neutral btn-sm"
                        aria-label={t('bookingList.viewFormAria', { id: booking.id })}
                      >
                        {t('bookingList.viewForm')}
                      </button>
                    )}
                    </fieldset>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedBooking && <DepositDetailsDialog booking={selectedBooking} onClose={() => setSelectedBooking(null)} />}
      {complaintTarget&&<DepositComplaints deposit={complaintTarget} admin={user.role==='admin'} onClose={()=>setComplaintTarget(null)} onChange={(complaints)=>{setComplaintTarget((previous)=>({...previous,complaints}));setBookings((previous)=>previous.map((booking)=>booking.id===complaintTarget.id?{...booking,complaints}:booking));}}/>}

      <ConfirmDialog
        open={Boolean(bookingToDelete)}
        title={t(deletingDraft ? 'bookingList.draftDeleteTitle' : 'bookingList.cancelTitle')}
        message={t(deletingDraft ? 'bookingList.draftDeleteMessage' : 'bookingList.confirmCancel')}
        confirmText={t(deletingDraft ? 'bookingList.draftDelete' : 'bookingList.cancel')}
        cancelText={t(deletingDraft ? 'bookingList.draftKeep' : 'common.cancel')}
        onConfirm={handleDelete}
        onCancel={() => setBookingToDelete(null)}
      />
    </div>
  );
};

export default BookingList;
